/**
 * Drag a row up or down a list: Radio's Up next, Flow's songs, a playlist.
 *
 * Native drag and drop, so it works with a mouse, a trackpad and the webview's
 * own drag image, and needs no library. The row under the pointer shows a line
 * where the song will land (above or below it, by which half the pointer is
 * in); dropping calls `onMove(from, to)` with `to` as the final index.
 *
 * `canDrag(i)` keeps rows in place (the song playing, a song already played).
 */

import { useState, type DragEvent } from "react";

const TYPE = "application/x-nova-row";

export function useDragReorder({
  onMove,
  canDrag = () => true,
  canDrop = canDrag,
}: {
  onMove: (from: number, to: number) => void;
  canDrag?: (i: number) => boolean;
  /** Whether a song may land at index `i` (defaults to the same rule as dragging). */
  canDrop?: (i: number) => boolean;
}) {
  const [from, setFrom] = useState<number | null>(null);
  const [over, setOver] = useState<{ i: number; after: boolean } | null>(null);

  const target = (i: number, after: boolean) => {
    if (from === null) return null;
    let to = after ? i + 1 : i;
    if (to > from) to -= 1;
    return to;
  };

  const rowProps = (i: number) => {
    const draggable = canDrag(i);
    const marker = over && over.i === i && from !== null && from !== i ? (over.after ? "drop-after" : "drop-before") : "";
    return {
      draggable,
      "data-drag-index": i,
      className: `${from === i ? "dragging" : ""} ${marker}`.trim(),
      onDragStart: (e: DragEvent) => {
        if (!draggable) return;
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData(TYPE, String(i));
        // Some webviews refuse a drag that carries no plain text.
        e.dataTransfer.setData("text/plain", "");
        setFrom(i);
      },
      onDragOver: (e: DragEvent) => {
        if (from === null) return;
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
        const after = e.clientY > rect.top + rect.height / 2;
        const to = target(i, after);
        if (to === null || !canDrop(to)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        if (!over || over.i !== i || over.after !== after) setOver({ i, after });
      },
      onDrop: (e: DragEvent) => {
        e.preventDefault();
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
        const to = target(i, e.clientY > rect.top + rect.height / 2);
        const start = from;
        setFrom(null);
        setOver(null);
        if (start !== null && to !== null && to !== start && canDrop(to)) onMove(start, to);
      },
      onDragEnd: () => {
        setFrom(null);
        setOver(null);
      },
    };
  };

  return { rowProps, dragging: from };
}

/** Move one element of a list. */
export function moved<T>(list: T[], from: number, to: number): T[] {
  const out = [...list];
  const [item] = out.splice(from, 1);
  out.splice(Math.max(0, Math.min(to, out.length)), 0, item);
  return out;
}
